import React, { useRef, useState } from 'react';

import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';

import AcCard from 'components/AcCard/AcCard';
import Badge from 'components/Badge/Badge';
import Input from 'components/Input/Input';
import Typography from 'components/Typography/Typography';
import UploadImage from 'components/UploadImage/UploadImage';

import { defaultStoreTheme } from '../defaults';
import { StoreThemeFormValues } from '../storeTheme.types';

const INPUT_FOCUS_STYLE = 'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-base-foreground';

export const SEOSection: React.FC = () => {
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<StoreThemeFormValues>();

  const defaultSEO = values?.seo || defaultStoreTheme.seo;

  const [keywordInput, setKeywordInput] = useState('');
  const keywordInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="border-t border-base-sidebarBorder">
      <AcCard
        stackContainer={false}
        title="SEO Settings"
        description="Customize how this page appears in search results and social media previews."
      >
        <div className="grid grid-cols-1 gap-y-6">
          <Input
            label="SEO Title"
            name="seo.title"
            value={defaultSEO?.title || ''}
            placeholder="Add a title"
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={errors.seo?.title ? 'error' : 'default'}
            description={
              errors.seo?.title?.toString() ||
              `${(defaultSEO?.title || '').length}/60 characters`
            }
            dataTestId="seoTitle"
            inputClassName={INPUT_FOCUS_STYLE}
          />
          <Input
            label="Description"
            name="seo.description"
            value={defaultSEO?.description || ''}
            placeholder="Write a short summary that describes this page for search engines."
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={errors.seo?.description ? 'error' : 'default'}
            description={
              errors.seo?.description?.toString() ||
              `${(defaultSEO?.description || '').length}/160 characters`
            }
            dataTestId="seoDescription"
            inputClassName={INPUT_FOCUS_STYLE}
          />
          <div>
            <Input
              ref={keywordInputRef}
              label="Keywords"
              placeholder="Search Keyword"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && keywordInput.trim()) {
                  e.preventDefault();
                  const current = defaultSEO?.keywords || [];
                  setFieldValue('seo.keywords', [
                    ...current,
                    keywordInput.trim()
                  ]);
                  setKeywordInput('');
                  keywordInputRef?.current?.blur();
                }
              }}
              dataTestId="seoKeywords"
              inputClassName={INPUT_FOCUS_STYLE}
            />
            <Typography className="text-zinc-500 text-sm mt-2">
              Press &apos;Enter&apos; to add a new keyword
            </Typography>
            {!!defaultSEO?.keywords?.length && (
              <div className="mt-3 flex flex-row flex-wrap gap-2">
                {defaultSEO.keywords.map((keyword: string) => (
                  <Badge
                    key={keyword}
                    isRemovable
                    onRemove={() =>
                      setFieldValue(
                        'seo.keywords',
                        defaultSEO.keywords.filter(
                          (k: string) => k !== keyword
                        )
                      )
                    }
                  >
                    {keyword}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <UploadImage
            title="SEO / Social Share Image"
            description="JPG, PNG — recommended size 1200×630px"
            buttonText="Upload"
            onUpload={(url: string) => {
              setFieldValue('seo.socialShareImageUrl', url);
            }}
            selectedImageUrl={defaultSEO?.socialShareImageUrl || ''}
            forceType={EAssetType.SEO}
            className="w-full"
            data-testid="seoSocialShareImage"
            buttonDataTestId="uploadSeoSocialShareImage"
          />
        </div>
        <Typography tag="h4" className="my-2 mt-6">
          Social Metadata Override
        </Typography>
        <div className="grid grid-cols-1 gap-y-6">
          <Input
            label="Social Title"
            name="seo.socialTitle"
            value={defaultSEO?.socialTitle || ''}
            placeholder="Social Title"
            onChange={handleChange}
            onBlur={handleBlur}
            state={
              touched.seo?.socialTitle && errors.seo?.socialTitle
                ? 'error'
                : 'default'
            }
            description={
              touched.seo?.socialTitle
                ? errors.seo?.socialTitle?.toString() || ''
                : ''
            }
            dataTestId="seoSocialTitle"
            inputClassName={INPUT_FOCUS_STYLE}
          />
          <Input
            label="Social Description"
            name="seo.socialDescription"
            value={defaultSEO?.socialDescription || ''}
            placeholder="If not set, SEO Description will be used"
            onChange={handleChange}
            onBlur={handleBlur}
            state={
              touched.seo?.socialDescription && errors.seo?.socialDescription
                ? 'error'
                : 'default'
            }
            description={
              touched.seo?.socialDescription
                ? errors.seo?.socialDescription?.toString() || ''
                : ''
            }
            dataTestId="seoSocialDescription"
            inputClassName={INPUT_FOCUS_STYLE}
          />
        </div>
      </AcCard>
    </div>
  );
};
